import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import AddressAutocompleteInput from "../components/AddressAutocompleteInput";
import CityAutocompleteInput from "../components/CityAutocompleteInput";
import { TIMEZONE_OPTIONS } from "../constants/timezones";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

// Utility function to convert text to user-friendly Title Case
const toUserFriendly = (text) => {
  if (!text) return "";
  // Convert kebab-case, snake_case, and camelCase to spaces
  return text
    .replace(/[-_]/g, " ") // Replace hyphens and underscores with spaces
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before capital letters (camelCase)
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

function FreelancerSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  // A code typed on the open site (house promo, or an agent's code). Hidden when the visitor arrived
  // through an agent's share link — the link already carries the code, and asking twice invites a clash.
  const [typedCode, setTypedCode] = useState("");
  const [arrivedByLink] = useState(() => {
    try {
      // THIS VISIT only. A code left in localStorage by an earlier test must not hide the field.
      return sessionStorage.getItem("ogestyle_referral_from_link") === "1";
    } catch (_err) {
      return false;
    }
  });
  // A code already captured on the account screen, so this one does not ask a second time.
  const [storedCode] = useState(() => {
    try {
      return localStorage.getItem("ogestyle_referral_code") || "";
    } catch (_err) {
      return "";
    }
  });

  // Check if this is a retry (incomplete registration)
  const isRetry = location.state?.isRetry;

  // API-first: Fetch from database, fallback to constants
  const [businessCategories, setBusinessCategories] = useState([]);
  const [allProfessionalTypes, setAllProfessionalTypes] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [formData, setFormData] = useState({
    businessName: "",
    businessCategories: [], // Multiple business categories
    professionalTypes: [], // Multiple professional types
    primaryProfessionalType: "", // Primary display type
    role: "", // Legacy field for API compatibility
    serviceTypes: [],
    country: "US", // Default to US
    businessAddress: {
      street: "",
      city: "",
      state: "",
      zip: "",
      showFullAddress: false,
    },
    serviceArea: {
      cities: [""],
      maxTravelDistance: 25,
      travelFee: 0,
    },
    timezone: "", // required and chosen by the freelancer - never pre-filled with a zone they did not pick
  });

  const serviceTypeOptions = [
    { value: "at_my_home", label: "🏠 At My Home Studio" },
    { value: "at_client_home", label: "🚗 At Client's Location" },
    { value: "mobile", label: "📍 Mobile (I Travel)" },
    { value: "at_salon", label: "💼 At Partner Salon" },
  ];

  // Fetch business categories and professional types from API on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch business categories
        const categoriesResponse = await fetch(
          `${API_BASE_URL}/api/platform-settings/business-categories`,
        );
        const categoriesData = await categoriesResponse.json();

        if (categoriesData.types && categoriesData.types.length > 0) {
          setBusinessCategories(categoriesData.types);
        } else {
          setBusinessCategories([]);
        }

        // Fetch professional types
        const typesResponse = await fetch(
          `${API_BASE_URL}/api/platform-settings/professional-types/public`,
        );
        const typesData = await typesResponse.json();

        if (
          typesData.professionalTypes &&
          typesData.professionalTypes.length > 0
        ) {
          setAllProfessionalTypes(typesData.professionalTypes);
        } else {
          // Fallback to helper function from constants
          setAllProfessionalTypes([]);
        }
      } catch (error) {
        setBusinessCategories([]);
        setAllProfessionalTypes([]);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleServiceTypeToggle = (type) => {
    setFormData((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(type)
        ? prev.serviceTypes.filter((t) => t !== type)
        : [...prev.serviceTypes, type],
    }));
  };

  const handleBusinessCategoryToggle = (category) => {
    setFormData((prev) => {
      const newCategories = prev.businessCategories.includes(category)
        ? prev.businessCategories.filter((c) => c !== category)
        : [...prev.businessCategories, category];
      return { ...prev, businessCategories: newCategories };
    });
  };

  const handleProfessionalTypeToggle = (type) => {
    setFormData((prev) => {
      const isCurrentlySelected = prev.professionalTypes.includes(type);
      const newTypes = isCurrentlySelected
        ? prev.professionalTypes.filter((t) => t !== type)
        : [...prev.professionalTypes, type].slice(0, 5); // Max 5 types

      // If removing the primary type, clear it
      const newPrimaryType =
        isCurrentlySelected && prev.primaryProfessionalType === type
          ? newTypes[0] || ""
          : prev.primaryProfessionalType;

      // Freelancers always use role code R4
      return {
        ...prev,
        professionalTypes: newTypes,
        primaryProfessionalType: newPrimaryType,
        role: "R4", // Freelancer role code
      };
    });
  };

  const handlePrimaryTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      primaryProfessionalType: type,
      role: "R4", // Freelancer role code
    }));
  };

  // Get filtered professional types based on selected business categories
  const availableProfessionalTypes = allProfessionalTypes.filter((type) =>
    formData.businessCategories.some((cat) =>
      type.businessCategories?.includes(cat),
    ),
  );

  const handleCityChange = (index, value) => {
    const newCities = [...formData.serviceArea.cities];
    newCities[index] = value;
    setFormData((prev) => ({
      ...prev,
      serviceArea: { ...prev.serviceArea, cities: newCities },
    }));
  };

  const addCity = () => {
    setFormData((prev) => ({
      ...prev,
      serviceArea: {
        ...prev.serviceArea,
        cities: [...prev.serviceArea.cities, ""],
      },
    }));
  };

  const removeCity = (index) => {
    setFormData((prev) => ({
      ...prev,
      serviceArea: {
        ...prev.serviceArea,
        cities: prev.serviceArea.cities.filter((_, i) => i !== index),
      },
    }));
  };

  const resolveAddressCoordinates = async (address) => {
    const street = String(address?.street || "").trim();
    const city = String(address?.city || "").trim();
    const state = String(address?.state || "").trim();
    const zip = String(address?.zip || "").trim();

    if (!street || !city || !state) {
      return null;
    }

    const endpoint = new URL("https://nominatim.openstreetmap.org/search");
    endpoint.searchParams.set("format", "jsonv2");
    endpoint.searchParams.set("limit", "1");
    endpoint.searchParams.set("countrycodes", "us");
    endpoint.searchParams.set(
      "q",
      [street, city, state, zip, "USA"].filter(Boolean).join(", "),
    );

    try {
      const response = await fetch(endpoint.toString(), {
        headers: {
          Accept: "application/json",
        },
      });
      if (!response.ok) {
        return null;
      }

      const rows = await response.json();
      if (!Array.isArray(rows) || rows.length === 0) {
        return null;
      }

      const best = rows[0];
      const lat = Number(best.lat);
      const lng = Number(best.lon);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
      }

      return {
        lat,
        lng,
      };
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    // Validate Terms & Privacy agreement
    if (!agreeToTerms) {
      setError("Please agree to the Terms of Service and Privacy Policy");
      setLoading(false);
      return;
    }

    // The server refuses a signup without a chosen zone; stop here with the same words instead.
    if (!formData.timezone) {
      setError("Please choose your timezone.");
      setLoading(false);
      return;
    }

    try {
      const hasCoordinates =
        formData.businessAddress.lat !== undefined &&
        formData.businessAddress.lat !== null &&
        formData.businessAddress.lat !== "" &&
        formData.businessAddress.lng !== undefined &&
        formData.businessAddress.lng !== null &&
        formData.businessAddress.lng !== "";

      let payload = formData;
      if (!hasCoordinates) {
        const resolvedCoordinates = await resolveAddressCoordinates(
          formData.businessAddress,
        );

        if (!resolvedCoordinates) {
          setError(
            "Coordinates (lat/lng) are required. Please pick your business address from the suggestions.",
          );
          setLoading(false);
          return;
        }

        payload = {
          ...formData,
          businessAddress: {
            ...formData.businessAddress,
            lat: resolvedCoordinates.lat,
            lng: resolvedCoordinates.lng,
          },
        };

        setFormData(payload);
      }

      // Arrived by an agent's link -> that code wins and no field is shown. Arrived on the open site ->
      // they may type a house promo or an agent's code instead.
      let linkCode = null;
      try {
        linkCode = localStorage.getItem("ogestyle_referral_code");
      } catch (_) {
        linkCode = null;
      }
      const referralCode = linkCode
        ? String(linkCode).trim().toUpperCase()
        : typedCode.trim()
          ? typedCode.trim().toUpperCase()
          : null;
      if (referralCode) {
        try {
          localStorage.setItem("ogestyle_referral_code", referralCode);
        } catch (_err) {
          /* storage disabled: the code is still sent with the registration */
        }
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/freelancer/register`,
        referralCode ? { ...payload, referralCode } : payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      alert("Freelancer profile created successfully!");

      // Signup is done: hand the code to the pricing page and clear the arrival markers so a later
      // signup on this browser is asked for a code again.
      try {
        if (referralCode) localStorage.setItem("ogestyle_promo_code", referralCode);
        localStorage.removeItem("ogestyle_referral_code");
        sessionStorage.removeItem("ogestyle_referral_from_link");
      } catch (_err) {
        /* storage disabled */
      }

      navigate("/dashboard/freelancer");
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#ffffff",
        padding: "90px 20px 40px 20px",
      }}
    >
      <div style={{ maxWidth: "700px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <h1
            style={{
              fontSize: "32px",
              fontWeight: "700",
              marginBottom: "8px",
              color: "#111827",
            }}
          >
            {isRetry
              ? "Complete Your Registration"
              : "Become a Freelance Professional"}
          </h1>
          <p style={{ fontSize: "16px", color: "#6b7280" }}>
            {isRetry
              ? "Let's finish setting up your profile so you can start accepting bookings"
              : "Set your own schedule, work from anywhere, keep 100% of your earnings"}
          </p>
          {isRetry && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px 20px",
                background: "#fef3c7",
                border: "1px solid #fbbf24",
                borderRadius: "8px",
                fontSize: "14px",
                color: "#92400e",
              }}
            >
              ℹ️ We noticed your registration wasn't completed. Please review
              and submit your information.
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "40px",
            gap: "24px",
          }}
        >
          {[1, 2, 3].map((num) => (
            <div
              key={num}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background:
                  step >= num
                    ? "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)"
                    : "#e5e7eb",
                color: step >= num ? "white" : "#9ca3af",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "600",
              }}
            >
              {num}
            </div>
          ))}
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          style={{
            background: "white",
            padding: "40px",
            borderRadius: "16px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          {error && (
            <div
              style={{
                background: "#fee2e2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                padding: "12px",
                borderRadius: "8px",
                marginBottom: "24px",
              }}
            >
              {error}
            </div>
          )}

          {/* Step 1: Basic Info */}
          {step === 1 && (
            <>
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "24px",
                  color: "#111827",
                }}
              >
                Professional Information
              </h3>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Business Name
                </label>
                <input
                  type="text"
                  value={formData.businessName}
                  onChange={(e) =>
                    handleInputChange("businessName", e.target.value)
                  }
                  placeholder="e.g., Sarah's Mobile Salon"
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Country
                </label>
                <select
                  value={formData.country}
                  onChange={(e) => handleInputChange("country", e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  <option value="US">🇺🇸 United States</option>
                  <option value="CA">🇨🇦 Canada</option>
                  <option value="GB">🇬🇧 United Kingdom</option>
                  <option value="NG">🇳🇬 Nigeria</option>
                  <option value="AU">🇦🇺 Australia</option>
                </select>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "12px",
                    color: "#374151",
                  }}
                >
                  Business Categories (select all that apply)
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: "10px",
                  }}
                >
                  {businessCategories.map((category) => (
                    <button
                      key={category.value}
                      type="button"
                      onClick={() =>
                        handleBusinessCategoryToggle(category.value)
                      }
                      style={{
                        padding: "12px 8px",
                        border: "2px solid",
                        borderColor: formData.businessCategories.includes(
                          category.value,
                        )
                          ? "#1a659e"
                          : "#e5e7eb",
                        borderRadius: "8px",
                        background: formData.businessCategories.includes(
                          category.value,
                        )
                          ? "#e8f4f8"
                          : "white",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "13px",
                        fontWeight: "500",
                        color: formData.businessCategories.includes(
                          category.value,
                        )
                          ? "#1a659e"
                          : "#6b7280",
                        transition: "all 0.2s",
                      }}
                    >
                      <span style={{ fontSize: "1.25rem" }}>
                        {category.emoji}
                      </span>
                      <span style={{ textAlign: "center" }}>
                        {category.label}
                      </span>
                    </button>
                  ))}
                </div>
                <small
                  style={{
                    display: "block",
                    marginTop: "8px",
                    color: "#6b7280",
                    fontSize: "12px",
                  }}
                >
                  Selected {formData.businessCategories.length} categories
                </small>
              </div>

              {formData.businessCategories.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "12px",
                      color: "#374151",
                    }}
                  >
                    Your Professional Types (select 1-5)
                  </label>
                  {availableProfessionalTypes.length > 0 ? (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gap: "8px",
                          maxHeight: "300px",
                          overflowY: "auto",
                          padding: "4px",
                        }}
                      >
                        {availableProfessionalTypes.map((type) => (
                          <label
                            key={type.value}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "12px",
                              border: "2px solid",
                              borderColor: formData.professionalTypes.includes(
                                type.value,
                              )
                                ? "#1a659e"
                                : "#e5e7eb",
                              borderRadius: "8px",
                              cursor: "pointer",
                              background: formData.professionalTypes.includes(
                                type.value,
                              )
                                ? "#e8f4f8"
                                : "white",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={formData.professionalTypes.includes(
                                type.value,
                              )}
                              onChange={() =>
                                handleProfessionalTypeToggle(type.value)
                              }
                              disabled={
                                formData.professionalTypes.length >= 5 &&
                                !formData.professionalTypes.includes(type.value)
                              }
                              style={{ marginRight: "12px" }}
                            />
                            <span
                              style={{ fontSize: "1.1rem", marginRight: "8px" }}
                            >
                              {type.emoji}
                            </span>
                            <span
                              style={{ fontSize: "14px", fontWeight: "500" }}
                            >
                              {type.label}
                            </span>
                          </label>
                        ))}
                      </div>
                      <small
                        style={{
                          display: "block",
                          marginTop: "8px",
                          color: "#6b7280",
                          fontSize: "12px",
                        }}
                      >
                        Selected {formData.professionalTypes.length}/5 types
                      </small>
                    </>
                  ) : (
                    <p style={{ color: "#6b7280", fontStyle: "italic" }}>
                      No professional types available for selected categories
                    </p>
                  )}
                </div>
              )}

              {formData.professionalTypes.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "12px",
                      color: "#374151",
                    }}
                  >
                    Primary Professional Type (shown first on profile)
                  </label>
                  <div style={{ display: "grid", gap: "8px" }}>
                    {formData.professionalTypes.map((type) => {
                      // Find the full type object from allProfessionalTypes to get icon
                      const typeObj = allProfessionalTypes.find(
                        (t) => t.type === type || t.name === type,
                      );
                      return (
                        <label
                          key={type}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "12px",
                            border: "2px solid",
                            borderColor:
                              formData.primaryProfessionalType === type
                                ? "#1a659e"
                                : "#e5e7eb",
                            borderRadius: "8px",
                            cursor: "pointer",
                            background:
                              formData.primaryProfessionalType === type
                                ? "#e8f4f8"
                                : "white",
                          }}
                        >
                          <input
                            type="radio"
                            name="primaryType"
                            checked={formData.primaryProfessionalType === type}
                            onChange={() => handlePrimaryTypeChange(type)}
                            style={{ marginRight: "12px" }}
                          />
                          <span
                            style={{ fontSize: "1.1rem", marginRight: "8px" }}
                          >
                            {typeObj?.icon || "👤"}
                          </span>
                          <span style={{ fontSize: "14px", fontWeight: "500" }}>
                            {toUserFriendly(type)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "12px",
                    color: "#374151",
                  }}
                >
                  Where Do You Provide Services?
                </label>
                <div style={{ display: "grid", gap: "12px" }}>
                  {serviceTypeOptions.map((option) => (
                    <label
                      key={option.value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "12px",
                        border: "2px solid",
                        borderColor: formData.serviceTypes.includes(
                          option.value,
                        )
                          ? "#1a659e"
                          : "#e5e7eb",
                        borderRadius: "8px",
                        cursor: "pointer",
                        background: formData.serviceTypes.includes(option.value)
                          ? "#e8f4f8"
                          : "white",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.serviceTypes.includes(option.value)}
                        onChange={() => handleServiceTypeToggle(option.value)}
                        style={{ marginRight: "12px" }}
                      />
                      <span style={{ fontSize: "15px" }}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={
                  !formData.businessName ||
                  formData.professionalTypes.length === 0 ||
                  !formData.primaryProfessionalType ||
                  formData.serviceTypes.length === 0
                }
                style={{
                  width: "100%",
                  padding: "14px",
                  background:
                    "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "16px",
                  cursor: "pointer",
                  opacity:
                    !formData.businessName ||
                    formData.professionalTypes.length === 0 ||
                    !formData.primaryProfessionalType ||
                    formData.serviceTypes.length === 0
                      ? 0.5
                      : 1,
                }}
              >
                Continue →
              </button>
            </>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <>
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "24px",
                  color: "#111827",
                }}
              >
                Business Address
              </h3>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Street Address
                </label>
                <AddressAutocompleteInput
                  value={formData.businessAddress.street}
                  onChange={(val) =>
                    handleInputChange("businessAddress.street", val)
                  }
                  onAddressSelect={({ street, city, state, zip, lat, lng }) => {
                    handleInputChange("businessAddress.street", street || "");
                    if (city) handleInputChange("businessAddress.city", city);
                    if (state) handleInputChange("businessAddress.state", state);
                    if (zip) handleInputChange("businessAddress.zip", zip);
                    handleInputChange("businessAddress.lat", lat || '');
                    handleInputChange("businessAddress.lng", lng || '');
                  }}
                  placeholder="123 W Madison St"
                  required
                  inputStyle={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "8px",
                    fontSize: "13px",
                    color: "#6b7280",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.businessAddress.showFullAddress}
                    onChange={(e) =>
                      handleInputChange(
                        "businessAddress.showFullAddress",
                        e.target.checked,
                      )
                    }
                    style={{ marginRight: "8px" }}
                  />
                  Show full address publicly (recommended for home studios)
                </label>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                      color: "#374151",
                    }}
                  >
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.businessAddress.city}
                    onChange={(e) =>
                      handleInputChange("businessAddress.city", e.target.value)
                    }
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                    placeholder="Chicago"
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                      color: "#374151",
                    }}
                  >
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.businessAddress.state}
                    onChange={(e) =>
                      handleInputChange("businessAddress.state", e.target.value)
                    }
                    maxLength="2"
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                    placeholder="IL"
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                      color: "#374151",
                    }}
                  >
                    ZIP
                  </label>
                  <input
                    type="text"
                    value={formData.businessAddress.zip}
                    onChange={(e) =>
                      handleInputChange("businessAddress.zip", e.target.value)
                    }
                    maxLength="5"
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{
                    flex: 1,
                    padding: "14px",
                    background: "white",
                    color: "#1a659e",
                    border: "2px solid #1a659e",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "16px",
                    cursor: "pointer",
                  }}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  disabled={
                    !formData.businessAddress.city ||
                    !formData.businessAddress.state
                  }
                  style={{
                    flex: 1,
                    padding: "14px",
                    background:
                      "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "16px",
                    cursor: "pointer",
                    opacity:
                      !formData.businessAddress.city ||
                      !formData.businessAddress.state
                        ? 0.5
                        : 1,
                  }}
                >
                  Continue →
                </button>
              </div>
            </>
          )}

          {/* Step 3: Service Area */}
          {step === 3 && (
            <>
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "24px",
                  color: "#111827",
                }}
              >
                Service Area & Travel
              </h3>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Cities You Serve
                </label>
                {formData.serviceArea.cities.map((city, index) => (
                  <div
                    key={index}
                    style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                  >
                    <CityAutocompleteInput
                      value={city}
                      onChange={(nextValue) => handleCityChange(index, nextValue)}
                      onCitySelect={(selectedCity) =>
                        handleCityChange(index, selectedCity.label)
                      }
                      placeholder="Chicago, IL"
                      inputStyle={{
                        flex: 1,
                        padding: "12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                      }}
                    />
                    {formData.serviceArea.cities.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCity(index)}
                        style={{
                          padding: "12px",
                          background: "#fee2e2",
                          color: "#991b1b",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addCity}
                  style={{
                    padding: "8px 16px",
                    background: "#e8f4f8",
                    color: "#1a659e",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  + Add City
                </button>
              </div>

              {(formData.serviceTypes.includes("mobile") ||
                formData.serviceTypes.includes("at_client_home")) && (
                <>
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: "500",
                        marginBottom: "8px",
                        color: "#374151",
                      }}
                    >
                      Max Travel Distance (miles)
                    </label>
                    <input
                      type="number"
                      value={formData.serviceArea.maxTravelDistance}
                      onChange={(e) =>
                        handleInputChange(
                          "serviceArea.maxTravelDistance",
                          parseInt(e.target.value),
                        )
                      }
                      min="1"
                      max="100"
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        fontWeight: "500",
                        marginBottom: "8px",
                        color: "#374151",
                      }}
                    >
                      Travel Fee ($)
                    </label>
                    <input
                      type="number"
                      value={formData.serviceArea.travelFee}
                      onChange={(e) =>
                        handleInputChange(
                          "serviceArea.travelFee",
                          parseFloat(e.target.value),
                        )
                      }
                      min="0"
                      step="5"
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                      }}
                    />
                  </div>
                </>
              )}

              {/* The server rejects a signup without a chosen IANA zone, so the form must ask for one. */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  Your Timezone *
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => handleInputChange("timezone", e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                    background: "white",
                  }}
                >
                  <option value="">Select your timezone...</option>
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                  Used so your bookings show the right times.
                </div>
              </div>

              {!arrivedByLink && !storedCode && (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                      color: "#374151",
                    }}
                  >
                    Referral or promo code
                  </label>
                  <input
                    type="text"
                    value={typedCode}
                    onChange={(e) => setTypedCode(e.target.value.toUpperCase())}
                    placeholder="Leave blank if you have none"
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                    A promotion code, or the code of the person who referred you.
                  </div>
                </div>
              )}

              {/* Terms & Privacy Agreement */}
              <div style={{ marginTop: "24px", marginBottom: "20px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    fontSize: "14px",
                    color: "#374151",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    required
                    style={{
                      marginTop: "4px",
                      cursor: "pointer",
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    I agree to the{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#1a659e",
                        textDecoration: "underline",
                      }}
                    >
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#1a659e",
                        textDecoration: "underline",
                      }}
                    >
                      Privacy Policy
                    </a>
                    , and to occasionally receive emails from OGESTYLE
                  </span>
                </label>
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  style={{
                    flex: 1,
                    padding: "14px",
                    background: "white",
                    color: "#1a659e",
                    border: "2px solid #1a659e",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "16px",
                    cursor: "pointer",
                  }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: "14px",
                    background:
                      "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "16px",
                    cursor: "pointer",
                    opacity: loading ? 0.5 : 1,
                  }}
                >
                  {loading ? "Creating Profile..." : "Complete Registration ✓"}
                </button>
              </div>
            </>
          )}
        </form>

        <div style={{ textAlign: "center", marginTop: "24px" }}>
          <Link
            to="/login"
            style={{
              color: "#1a659e",
              textDecoration: "none",
              fontSize: "14px",
            }}
          >
            Already have an account? Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}

export default FreelancerSignup;
